/**
 * Product-tree arithmetic shared by the W-05 tree, the draft editor and the
 * mobile cost card. Everything here mirrors what `private.bom_cost_calc` and
 * `fn_bom_tree` (migration 20260923000002) already compute on the server — the
 * screens use it to label what the server returned, never to replace it.
 */
import { D, roundTo, type Dec, type DecimalInput } from './money';

/**
 * Gross requirement from a net one (brief §4.1 yield form, decision C62):
 * `net / (1 − scrap%)`. 8,330 kg at %2,4 fire → 8,535 kg of bobbin issued.
 * A scrap percentage of 100 or more has no meaningful yield, so it is refused
 * rather than silently returning infinity; the column is capped at 50 in SQL.
 */
export function grossQty(net: DecimalInput, scrapPct: DecimalInput, scale = 6): Dec {
  const pct = D(scrapPct);
  if (pct.greaterThanOrEqualTo(100) || pct.lessThan(0)) throw new RangeError('Fire yüzdesi 0 ile 100 arasında olmalı');
  return roundTo(D(net).dividedBy(D(1).minus(pct.dividedBy(100))), scale);
}

/** One operation row of a BOM, as PostgREST delivers it (numerics arrive as numbers). */
export interface BomOperationLike {
  minutes_per_1000?: number | string | null;
  setup_minutes?: number | string | null;
}

/**
 * "Toplam süre 18,7 dk/1000 adet" — the sum of the per-1000 minutes.
 * Setup minutes are a per-run cost, not a per-1000 one, so they stay out of this
 * figure (they are part of the cost roll-up, which scales them by `base_qty`).
 */
export function bomTotalMinutes(ops: BomOperationLike[] | null | undefined, scale = 3): Dec {
  let total = D(0);
  for (const o of ops ?? []) total = total.plus(D(o.minutes_per_1000 ?? 0));
  return roundTo(total, scale);
}

/**
 * Signed percentage change between two versions' costs, for the activation
 * modal and the drift band ("1.731,13 → 1.694,18 ₺ (−%2,1)"). Returns null when
 * the old value is missing or zero — a change from nothing is not a percentage.
 */
export function costChangePct(from: DecimalInput, to: DecimalInput, scale = 1): Dec | null {
  const a = D(from);
  if (a.isZero()) return null;
  return roundTo(D(to).minus(a).dividedBy(a).times(100), scale);
}
